import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "@/lib/utils"
import { isTouchViewport } from "@/hooks/use-mobile"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

// Browsers without :focus-visible keep the tooltip, as before.
const isFocusVisible = (el: Element) => {
  try {
    return el.matches(":focus-visible")
  } catch {
    return true
  }
}

type TooltipTriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>

/**
 * Sits between Radix's trigger and the button, where it can leave out Radix's
 * focus handler (the one that opens the tooltip) without marking the event:
 * a tab wrapped inside and the tablist above still see an ordinary focus.
 */
const TriggerFocusGate = React.forwardRef<
  HTMLButtonElement,
  TooltipTriggerProps & { onOwnFocus?: TooltipTriggerProps["onFocus"] }
>(({ asChild, onOwnFocus, onFocus, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      ref={ref}
      {...props}
      onFocus={(event: React.FocusEvent<HTMLButtonElement>) => {
        onOwnFocus?.(event)
        if (isTouchViewport() && !isFocusVisible(event.currentTarget)) return
        onFocus?.(event)
      }}
    />
  )
})
TriggerFocusGate.displayName = "TooltipTriggerFocusGate"

/**
 * Radix opens the tooltip on any focus but a press on the trigger itself. On
 * a touch screen that includes a closed dialog or sheet handing focus back,
 * which would leave the tooltip up with nothing to hover off, so there it
 * only opens for a keyboard (:focus-visible). Hover and wide screens with a
 * mouse are unchanged.
 */
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ asChild, onFocus, children, ...props }, ref) => (
  <TooltipPrimitive.Trigger ref={ref} {...props} asChild>
    <TriggerFocusGate asChild={asChild} onOwnFocus={onFocus}>
      {children}
    </TriggerFocusGate>
  </TooltipPrimitive.Trigger>
))
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
